// State-aware unit library. Extends the base units.js with regional units.
import { STATE_UNIT_FACTORS } from "./states";

// Base sqm-per-unit values used when state has no override.
export const BASE_UNITS = [
  { key: "sqft",    label: "Square Feet",     hi: "वर्ग फीट",   short: "sq ft",  sqm: 0.09290304, group: "metric" },
  { key: "sqyd",    label: "Square Yard (Gaj)", hi: "गज",       short: "sq yd",  sqm: 0.83612736, group: "metric" },
  { key: "sqm",     label: "Square Metre",    hi: "वर्ग मीटर",  short: "m²",     sqm: 1,          group: "metric" },
  { key: "acre",    label: "Acre",            hi: "एकड़",       short: "ac",     sqm: 4046.8564,  group: "metric" },
  { key: "hectare", label: "Hectare",         hi: "हेक्टेयर",    short: "ha",     sqm: 10000,      group: "metric" },
  { key: "dismil",  label: "Dismil (Decimal)", hi: "डिस्मिल",  short: "dismil", sqm: 40.468564,  group: "north" },
  { key: "bigha",   label: "Bigha",           hi: "बीघा",      short: "bigha",  sqm: 2529.2848,  group: "north", regional: true },
  { key: "biswa",   label: "Biswa",           hi: "बिस्वा",     short: "biswa",  sqm: 126.4642,   group: "north", regional: true },
  { key: "katha",   label: "Katha",           hi: "कट्ठा",      short: "katha",  sqm: 126.4642,   group: "north", regional: true },
  { key: "chatak",  label: "Chatak",          hi: "छटाक",       short: "chatak", sqm: 4.18,       group: "east",  regional: true },
  { key: "marla",   label: "Marla",           hi: "मरला",       short: "marla",  sqm: 25.29285,   group: "north" },
  { key: "kanal",   label: "Kanal",           hi: "कनाल",       short: "kanal",  sqm: 505.857,    group: "north" },
  { key: "guntha",  label: "Guntha",          hi: "गुंठा",       short: "guntha", sqm: 101.17141,  group: "west" },
  { key: "cent",    label: "Cent",            hi: "सेंट",        short: "cent",   sqm: 40.468564,  group: "south" },
  { key: "ground",  label: "Ground",          hi: "ग्राउंड",     short: "ground", sqm: 222.967,    group: "south" },
  { key: "ankanam", label: "Ankanam",         hi: "अंकणम",     short: "ankanam",sqm: 6.6889,     group: "south" },
  { key: "ropani",  label: "Ropani",          hi: "रोपणी",       short: "ropani", sqm: 508.72,     group: "north-east" },
  { key: "vigha",   label: "Vigha (Gujarat)", hi: "विघा",       short: "vigha",  sqm: 1618.7,     group: "west", regional: true },
];

// Returns the sqm value of 1 `unitKey` for a given state.
export function unitSqm(unitKey, stateCode) {
  const base = BASE_UNITS.find((u) => u.key === unitKey);
  if (!base) return 1;
  const override = STATE_UNIT_FACTORS[stateCode]?.[unitKey];
  return override ?? base.sqm;
}

// Which units are locally relevant to a state (used for compact display).
export function unitsForState(stateCode) {
  const eastern = ["WB", "AS", "OD", "BR", "JH"];
  const western = ["GJ", "MH", "GA"];
  const southern = ["TN", "KL", "AP", "TG", "KA", "PY"];
  const northern = ["UP", "UK", "HR", "PB", "DL", "HP", "JK", "RJ", "MP", "CG", "CH", "LA"];
  const universal = ["sqft", "sqyd", "sqm", "acre", "hectare"];
  const regional = {
    north: ["dismil", "bigha", "biswa", "katha", "marla", "kanal"],
    east:  ["katha", "chatak", "bigha", "dismil"],
    west:  ["bigha", "vigha", "guntha"],
    south: ["cent", "ground", "ankanam", "guntha"],
  };
  let keys = [...universal];
  if (northern.includes(stateCode)) keys = keys.concat(regional.north);
  else if (eastern.includes(stateCode)) keys = keys.concat(regional.east);
  else if (western.includes(stateCode)) keys = keys.concat(regional.west);
  else if (southern.includes(stateCode)) keys = keys.concat(regional.south);
  else keys = keys.concat(regional.north); // sensible default
  keys.push("marla", "kanal"); // useful across; dedupe
  return [...new Set(keys)].map((k) => BASE_UNITS.find((u) => u.key === k)).filter(Boolean);
}

// Convert `value` from unit `from` to unit `to`, both in the given `stateCode`.
export function convertState(value, from, to, stateCode) {
  const v = Number(value);
  if (!isFinite(v)) return 0;
  const sqm = v * unitSqm(from, stateCode);
  return sqm / unitSqm(to, stateCode);
}

export function convertAllState(value, from, stateCode) {
  const list = unitsForState(stateCode);
  const out = {};
  for (const u of list) out[u.key] = convertState(value, from, u.key, stateCode);
  return out;
}
